import { useEffect, useMemo, useState } from "react";

import { findCuratedDoseLimit } from "data/medicationDoseLimits";
import { drugReferenceService, lookupId } from "services/drugReferenceService";

const nameOf = (m) => m?.name || m?.medicationName;

/**
 * The dose reference for one medication, or null. A curated entry is returned on the first
 * render; an openFDA one arrives once its drugReference document has been read.
 */
export function useDoseLimit({ rxcui, name } = {}) {
  const curated = findCuratedDoseLimit({ rxcui, name });
  const id = lookupId({ rxcui, name });
  const [fetched, setFetched] = useState({ id: null, limit: null });

  useEffect(() => {
    if (curated || !id) return undefined;
    let live = true;
    drugReferenceService.getDoseLimit({ rxcui, name }).then((limit) => {
      if (live) setFetched({ id, limit });
    });
    return () => {
      live = false;
    };
    // id stands in for rxcui and name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, curated]);

  if (curated) return curated;
  // Ignore a result for a medication the form has since moved away from.
  return fetched.id === id ? fetched.limit : null;
}

/**
 * `medications` with each entry's dose reference attached as `doseLimit` (absent when there is
 * none), for code that has to stay synchronous — buildPatientRecordBlock reads it from there.
 * Returns the input array unchanged until something is found.
 */
export function useMedicationsWithDoseLimits(medications) {
  const [limits, setLimits] = useState({});

  const lookups = useMemo(() => {
    const byId = new Map();
    for (const m of medications || []) {
      const id = lookupId({ rxcui: m?.rxcui, name: nameOf(m) });
      if (id && !byId.has(id)) byId.set(id, { rxcui: m?.rxcui, name: nameOf(m) });
    }
    return byId;
  }, [medications]);
  const lookupsKey = [...lookups.keys()].join("|");

  useEffect(() => {
    if (!lookups.size) return undefined;
    let live = true;
    const ids = [...lookups.keys()];
    Promise.all(ids.map((id) => drugReferenceService.getDoseLimit(lookups.get(id)))).then(
      (found) => {
        if (!live) return;
        setLimits(Object.fromEntries(ids.map((id, i) => [id, found[i]])));
      }
    );
    return () => {
      live = false;
    };
    // lookupsKey stands in for lookups: a fresh snapshot with the same entries must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupsKey]);

  return useMemo(() => {
    if (!medications?.length) return medications;
    let changed = false;
    const enriched = medications.map((m) => {
      const doseLimit =
        findCuratedDoseLimit({ rxcui: m?.rxcui, name: nameOf(m) }) ||
        limits[lookupId({ rxcui: m?.rxcui, name: nameOf(m) })] ||
        null;
      if (!doseLimit) return m;
      changed = true;
      return { ...m, doseLimit };
    });
    return changed ? enriched : medications;
  }, [medications, limits]);
}
